import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

const MFCalculator = () => {
  const [activeTab, setActiveTab] = useState("sip");

  // SIP States
  const [sipAmount, setSipAmount] = useState(5000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(10);

  // Lumpsum States
  const [lumpAmount, setLumpAmount] = useState(100000);
  const [lumpRate, setLumpRate] = useState(12);
  const [lumpYears, setLumpYears] = useState(10);

  // Colors
  const COLORS = ["#00C49F", "#FFBB28"];

  // =============== SIP Calculation ===============
  const sipMonths = sipYears * 12;
  const sipR = sipRate / 100 / 12;

  const sipInvested = sipAmount * sipMonths;
  const sipFuture =
    sipAmount * ((Math.pow(1 + sipR, sipMonths) - 1) / sipR) * (1 + sipR);
  const sipReturns = sipFuture - sipInvested;

  // SIP Growth Chart Data
  const sipChartData = useMemo(() => {
    let data = [];
    for (let i = 1; i <= sipYears; i++) {
      const months = i * 12;
      const value =
        sipAmount * ((Math.pow(1 + sipR, months) - 1) / sipR) * (1 + sipR);

      data.push({ year: `Year ${i}`, value: Math.round(value) });
    }
    return data;
  }, [sipAmount, sipRate, sipYears]);

  // =============== Lumpsum Calculation ===============
  const lumpFuture =
    lumpAmount * Math.pow(1 + lumpRate / 100 / 12, lumpYears * 12);
  const lumpReturns = lumpFuture - lumpAmount;

  // Lumpsum Growth Chart Data
  const lumpChartData = useMemo(() => {
    let data = [];
    for (let i = 1; i <= lumpYears; i++) {
      const value = lumpAmount * Math.pow(1 + lumpRate / 100 / 12, i * 12);

      data.push({ year: `Year ${i}`, value: Math.round(value) });
    }
    return data;
  }, [lumpAmount, lumpRate, lumpYears]);

  return (
    <div className="w-full border border-black-dark-200 rounded-3xl text-white p-8 mt-16">
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        <h2 className="text-4xl font-bold text-center mb-10">SIP Calculator</h2>
        <p className="text-gray-300 text-center mb-12">
          The SIP calculator helps estimate the potential growth of your
          Systematic Investment Plan (SIP) investment over your chosen time
          frame. SIP is a convenient method to save for your long-term financial
          goals.
        </p>
        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12 mt-8">
          <button
            onClick={() => setActiveTab("sip")}
            className={`px-6 py-2 rounded-xl border ${
              activeTab === "sip"
                ? "bg-primary1 border-primary1"
                : "border-black-dark-200  border-black-dark-300"
            }`}
          >
            SIP Investment Amount
          </button>

          <button
            onClick={() => setActiveTab("lumpsum")}
            className={`px-6 py-2 rounded-xl border ${
              activeTab === "lumpsum"
                ? "bg-primary1 border-primary1"
                : "border-black-dark-200  border-black-dark-300"
            }`}
          >
            Lumpsum Investment Amount
          </button>
        </div>

        {/* ======================= SIP TAB ======================= */}
        {activeTab === "sip" && (
          <>
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div>
                <label className="text-gray-300">Monthly SIP (₹)</label>
                <input
                  type="number"
                  value={sipAmount}
                  onChange={(e) => setSipAmount(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border border-black-dark-200 mt-2"
                />
              </div>

              <div>
                <label className="text-gray-300">Return (% p.a.)</label>
                <input
                  type="number"
                  value={sipRate}
                  onChange={(e) => setSipRate(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border-black-dark-200 mt-2"
                />
              </div>

              <div>
                <label className="text-gray-300">Years</label>
                <input
                  type="number"
                  value={sipYears}
                  onChange={(e) => setSipYears(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border-black-dark-200 mt-2"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-16">
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <p className="text-gray-400">Total Invested</p>
                <h3 className="text-3xl font-bold">
                  ₹{sipInvested.toLocaleString()}
                </h3>
              </div>
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <p className="text-gray-400">Total Returns</p>
                <h3 className="text-3xl font-bold">
                  ₹{Math.round(sipReturns).toLocaleString()}
                </h3>
              </div>
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <p className="text-gray-400">Future Value</p>
                <h3 className="text-3xl font-bold">
                  ₹{Math.round(sipFuture).toLocaleString()}
                </h3>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Line Chart */}
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <h3 className="text-xl font-semibold mb-4">Growth Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={sipChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis dataKey="year" stroke="#ccc" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#00C49F"
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Pie Chart */}
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <h3 className="text-xl font-semibold mb-4">
                  Investment Breakdown
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Invested", value: sipInvested },
                        { name: "Returns", value: sipReturns },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {COLORS.map((c, i) => (
                        <Cell key={i} fill={c} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* ======================= LUMPSUM TAB ======================= */}
        {activeTab === "lumpsum" && (
          <>
            {/* Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div>
                <label className="text-gray-300">Investment Amount (₹)</label>
                <input
                  type="number"
                  value={lumpAmount}
                  onChange={(e) => setLumpAmount(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border  border-black-dark-200 mt-2"
                />
              </div>

              <div>
                <label className="text-gray-300">Return (% p.a.)</label>
                <input
                  type="number"
                  value={lumpRate}
                  onChange={(e) => setLumpRate(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border  border-black-dark-200 mt-2"
                />
              </div>

              <div>
                <label className="text-gray-300">Years</label>
                <input
                  type="number"
                  value={lumpYears}
                  onChange={(e) => setLumpYears(Number(e.target.value))}
                  className="no-scrollbar w-full p-3 bg-black-dark-100 rounded-lg border  border-black-dark-200 mt-2"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-16">
              <div className="bg-black-dark-100 p-6 rounded-xl border border-black-dark-200">
                <p className="text-gray-400">Invested</p>
                <h3 className="text-3xl font-bold">
                  ₹{lumpAmount.toLocaleString()}
                </h3>
              </div>
              <div className="bg-black-dark-100 p-6 rounded-xl border  border-black-dark-200">
                <p className="text-gray-400">Returns</p>
                <h3 className="text-3xl font-bold">
                  ₹{Math.round(lumpReturns).toLocaleString()}
                </h3>
              </div>
              <div className="bg-black-dark-100 p-6 rounded-xl border  border-black-dark-200">
                <p className="text-gray-400">Future Value</p>
                <h3 className="text-3xl font-bold">
                  ₹{Math.round(lumpFuture).toLocaleString()}
                </h3>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Line Chart */}
              <div className="bg-black-dark-100 p-6 rounded-xl border  border-black-dark-200">
                <h3 className="text-xl font-semibold mb-4">Growth Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={lumpChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                    <XAxis dataKey="year" stroke="#ccc" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#00C49F"
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Pie Chart */}
              <div className="bg-black-dark-100 p-6 rounded-xl border  border-black-dark-200">
                <h3 className="text-xl font-semibold mb-4">
                  Investment Breakdown
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Invested", value: lumpAmount },
                        { name: "Returns", value: lumpReturns },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {COLORS.map((c, i) => (
                        <Cell key={i} fill={c} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MFCalculator;
